/**
 * The agent's work, as it happens.
 *
 * WHAT THIS SHOWS: each tool call, the ARGUMENTS it was called with, what came back,
 * how long it took, and whether it failed. The arguments are the half that makes a
 * trace readable — "search_documents" twelve times is not a story;
 * `query="belanja modal TIK 2025"` is, because a reader can tell whether the agent
 * looked for the right thing long before the answer arrives.
 *
 * WHAT IT DOES NOT SHOW: the model's reasoning tokens. Those are ungated model prose,
 * and putting them on screen would defeat the rule the whole streaming design keeps —
 * that no model-authored ANSWER text reaches a reader before the Policy Guard clears
 * it. A tool's arguments are not the answer.
 *
 * NOTHING HERE IS INVENTED. No percentage, no progress bar, no fabricated step. The
 * clock is real elapsed time; a step is "running" only while it actually is.
 */
import { useTranslation } from "react-i18next";

import { clsx } from "@/lib/clsx.ts";

export interface TraceStep {
  /** The agent's own call index, so a `tool.finished` can find its `tool.started`. */
  call: number;
  tool: string;
  arguments?: Record<string, unknown>;
  ok?: boolean;
  ms?: number;
  summary?: string;
  passages?: number;
  done: boolean;
}

/**
 * ONE ROW PER DISTINCT CALL — same tool AND same arguments — carrying how many times it
 * was made.
 *
 * The agent really does repeat a byte-identical call until its budget is gone
 * (`loop.py` detects the repeat, counts it, and runs it anyway), so twelve rows saying
 * exactly the same thing is a TRUE report of a wasteful loop and an unreadable one.
 *
 * Grouping is by IDENTITY, not adjacency: `A B A A` shows two rows, `A ×3` and `B ×1`.
 * Position is not what a reader is asking — "what did it look for, and how often" is —
 * and a repeat that happens to be separated by another call is the same waste as one
 * that is not.
 *
 * Nothing is hidden. `×12` is the most visible thing about such a run, which is exactly
 * right: the repetition is the story. The AGENT behaviour is an answer-path change and
 * needs the judged golden-seed run behind it, so it is filed rather than patched here.
 */
export function collapseRepeats(steps: TraceStep[]): Array<TraceStep & { repeats: number }> {
  const byCall = new Map<string, TraceStep & { repeats: number }>();
  const order: string[] = [];

  for (const step of steps) {
    const key = `${step.tool}::${JSON.stringify(step.arguments ?? {})}`;
    const seen = byCall.get(key);
    if (!seen) {
      byCall.set(key, { ...step, repeats: 1 });
      order.push(key);
      continue;
    }
    seen.repeats += 1;
    // The LATEST outcome wins: a call still in flight must not read as finished
    // because an identical earlier one completed.
    seen.done = step.done;
    seen.ok = step.ok;
    // Time is SUMMED, because the waste is the point — three identical calls cost
    // three times the seconds, and showing one call's duration would hide that.
    seen.ms = (seen.ms ?? 0) + (step.ms ?? 0);
    seen.summary = step.summary ?? seen.summary;
  }

  return order.map((key) => byCall.get(key)!);
}

/** `query="belanja modal TIK 2025", top_k=8` — the call as it would be written. */
export function formatArguments(args: Record<string, unknown> | undefined): string {
  if (!args) return "";
  return Object.entries(args)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) =>
      typeof value === "string" ? `${key}="${value}"` : `${key}=${JSON.stringify(value)}`,
    )
    .join(", ");
}

function seconds(ms: number, language: string): string {
  return new Intl.NumberFormat(language === "en" ? "en-GB" : "id-ID", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ms / 1000);
}

export function AgentTrace({
  steps,
  elapsedSeconds,
  running,
  collapsed,
  onToggle,
}: {
  steps: TraceStep[];
  elapsedSeconds: number;
  running: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();

  // Before the first tool lands there is genuinely nothing to report, and saying so is
  // better than an empty box that looks broken.
  if (steps.length === 0) {
    return (
      <div className="trace" role="status" aria-live="polite">
        <div className="trace-head">
          <span className="pulse" aria-hidden="true" />
          <span className="trace-title">{t("chat.waiting")}</span>
          <span className="spacer" />
          <span className="wait-elapsed">{t("chat.elapsed", { seconds: elapsedSeconds })}</span>
        </div>
      </div>
    );
  }

  if (collapsed) {
    const distinct = collapseRepeats(steps).length;
    return (
      <button type="button" className="trace-receipt" onClick={onToggle} aria-expanded={false}>
        <ChevronRight />
        <span>{t("trace.trail")}</span>
        {/* Both numbers, because they are different facts: how many calls were made,
            and how many distinct things were actually asked. */}
        <span className="faint">
          {t("trace.receipt", { count: steps.length })}
          {distinct !== steps.length ? ` · ${t("trace.distinct", { count: distinct })}` : ""}
        </span>
      </button>
    );
  }

  return (
    <section className="trace" aria-label={t("trace.title")}>
      <button type="button" className="trace-head" onClick={onToggle} aria-expanded>
        {running ? <span className="pulse" aria-hidden="true" /> : null}
        <span className="trace-title">{running ? t("trace.working") : t("trace.title")}</span>
        <span className="spacer" />
        <span className="wait-elapsed">{t("chat.elapsed", { seconds: elapsedSeconds })}</span>
      </button>

      <ol className="trace-steps">
        {collapseRepeats(steps).map((step) => {
          const args = formatArguments(step.arguments);
          return (
            <li
              key={step.call}
              className={clsx(
                "step",
                !step.done && "run",
                step.done && step.ok === false && "fail",
                step.done && step.ok !== false && "done",
              )}
            >
              <span className="step-dot" aria-hidden="true">
                {step.done && step.ok !== false ? <StepTick /> : null}
              </span>
              <div className="step-body">
                <div className="step-name">
                  {step.tool}
                  {step.repeats > 1 ? (
                    <span className="step-repeat" title={t("trace.repeatedHelp")}>
                      ×{step.repeats}
                    </span>
                  ) : null}
                </div>
                {/* The call itself, in mono: it is an identifier, not prose. */}
                {args ? <div className="step-call">{args}</div> : null}
                {step.summary ? <div className="step-res">{step.summary}</div> : null}
              </div>
              <span className="step-t">
                {step.ms !== undefined
                  ? t("chat.elapsed", { seconds: seconds(step.ms, i18n.language) })
                  : ""}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function ChevronRight() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StepTick() {
  return (
    <svg width="8" height="8" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M3.5 8.4l3 3 6-6.4"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
